import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  AfterViewInit,
} from '@angular/core';
import { ResourceService } from '../core/services/resource.service';
import { XpathBuilderComponent } from '../core/components/xpath-builder/xpath-builder.component';
import { Subscription, EMPTY, of } from 'rxjs';
import {
  ResourceTableConfig,
  ModalType,
} from '../core/models/componentContract.model';
import { ResourceTableComponent } from '../core/components/resource-table/resource-table.component';
import {
  AttributeResource,
  AuthMode,
  MenuEvent,
  PopupConfig,
  XPathQuery,
} from '../core/models/dataContract.model';
import { TransService } from '../core/models/translation.model';
import { ConfigService } from '../core/services/config.service';
import { AttributePickerComponent } from '../core/components/attribute-picker/attribute-picker.component';
import { WindowCloseResult } from '@progress/kendo-angular-dialog';
import { switchMap, tap, finalize, catchError } from 'rxjs/operators';
import { ModalService } from '../core/services/modal.service';
import { UtilsService } from '../core/services/utils.service';
import { MatDialogRef } from '@angular/material/dialog';
import { ModalComponent } from '../core/components/modal/modal.component';
import { ActionMenuComponent } from '../core/components/action-menu/action-menu.component';
import { SwapService } from '../core/services/swap.service';

@Component({
  selector: 'app-advanced-search',
  templateUrl: './advanced-search.component.html',
  styleUrls: ['./advanced-search.component.scss'],
  standalone: false
})
export class AdvancedSearchComponent
  implements OnInit, AfterViewInit, OnDestroy {
  private subscription = new Subscription();

  @ViewChild('resourceTable')
  resourceTable: ResourceTableComponent;

  @ViewChild('xpathBuilder') xBuilder: XpathBuilderComponent;

  @ViewChild('actionMenu')
  actionMenu: ActionMenuComponent;

  customViewSetting: any;
  errorMsg = '';
  hasError = false;

  queryType = '*';
  selectedAttributes: Array<string> = ['DisplayName'];
  attributePickerSelectedAttributes: Array<string> = [];

  tableConfig: ResourceTableConfig;
  savedQueries: {
    [id: string]: {
      query: XPathQuery;
      attributes: Array<string>;
      xpath: string;
    };
  } = {};
  selectedQuery: any;

  resultantQuery: string;

  get selectedItems() {
    if (this.resourceTable) {
      return this.resourceTable.selection.length > 0
        ? this.resourceTable.selection
        : undefined;
    } else {
      return undefined;
    }
  }

  private getObjectTypeFromQuery(query: string) {
    const endPos = query.indexOf('[');
    return query.substring(1, endPos > 0 ? endPos : query.length);
  }

  /**
   * Handles changes to the object type by updating the table configuration and resource table.
   *
   * This method performs the following steps:
   * 1. Checks if the resource table is available. If not, it returns early.
   * 2. Retrieves the table configuration columns and the object schema based on the query type.
   * 3. Filters the selected attributes to include only those present in the object schema.
   * 4. Adds new columns to the table configuration for each selected attribute that is not already present.
   * 5. Filters out columns from the table configuration that are not in the new selected attributes.
   * 6. Updates the table configuration columns and export attributes.
   * 7. Updates the resource table data source.
   *
   * @private
   */
  private objectTypeChange() {
    if (!this.resourceTable) {
      return;
    }
    let tableConfigColumns = this.tableConfig.columns;
    const objectSchema = this.resource.schema[this.queryType].bindings;

    // Get the selected attributes which are in objectSchema
    const newSelectedAttributes = this.attributePickerSelectedAttributes.filter(
      (attr) => objectSchema.hasOwnProperty(attr)
    );

    //Add new columns to the tableConfig
    newSelectedAttributes.forEach((attr) => {
      if (tableConfigColumns.findIndex((col) => col.field === attr) < 0) {
        this.tableConfig.columns.push({
          field: attr,
          width: 200,
          sortable: true,
        });
      }
      //Filter out the tableConfig columns that are not in the newSelectedAttributes
      else {
        tableConfigColumns = tableConfigColumns.filter((col) =>
          newSelectedAttributes.includes(col.field)
        );
      }

      this.tableConfig.columns = tableConfigColumns;
      this.tableConfig.exportAttributes = this.tableConfig.columns.map(
        (c) => c.field
      );
      this.resourceTable.updateDataSource(true, false, true);
    });
  }

  constructor(
    private resource: ResourceService,
    private modal: ModalService,
    private utils: UtilsService,
    private translate: TransService,
    private config: ConfigService,
    private swap: SwapService
  ) { }

  ngOnInit(): void {
    this.customViewSetting = this.resource.customViewSetting;
    if (this.customViewSetting.hasOwnProperty('savedQueries')) {
      this.savedQueries = this.customViewSetting.savedQueries;
    }

    this.tableConfig = new ResourceTableConfig();
    this.tableConfig.cellPadding = 5;
    this.tableConfig.pageSize = 20;
    this.tableConfig.selectable = true;
    this.tableConfig.checkboxSelectOnly = false;
    this.tableConfig.selectMode = 'multiple';
    this.tableConfig.selectBoxWidth = 8;
    this.tableConfig.resizable = true;
    this.tableConfig.objectType = 'Person';
    // column attribute name should be case-sensitive because of the export-to-excel
    this.tableConfig.columns = [
      {
        field:
          this.resource.authenticationMode === AuthMode.azure
            ? 'displayname'
            : 'DisplayName',
        width: null,
        filterable: false,
        filter: 'text',
        sortable: true,
        locked: false,
      },
    ];

    this.resultantQuery = undefined;
  }

  ngAfterViewInit(): void {
    // Send build menu event
    setTimeout(() => {
      this.swap.menuEvent(
        new MenuEvent('build', 'search', '', null, this.actionMenu)
      );
    });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  onSearch(
    builder: XpathBuilderComponent,
    attributePicker: AttributePickerComponent
  ) {
    if (builder && builder.query) {
      this.errorMsg = '';
      this.hasError = false;
      this.subscription.add(
        builder.getXpath().subscribe(
          (result: string) => {
            if (result) {
              this.tableConfig.query = result;
              this.resultantQuery = result;
              this.attributePickerSelectedAttributes = [];
              const objectType = this.getObjectTypeFromQuery(result);
              const reloadAttributePicker = this.queryType !== objectType;
              attributePicker.value.forEach((item: any) =>
                this.attributePickerSelectedAttributes.push(item.systemName)
              );
              this.queryType = objectType;
              this.tableConfig.objectType =
                objectType && objectType !== '*' ? objectType : 'Person';
              this.tableConfig.linkNoneForm = this.queryType === 'event';
              if (reloadAttributePicker && attributePicker) {
                setTimeout(() => {
                  attributePicker.preSelectedItems =
                    this.attributePickerSelectedAttributes;
                  attributePicker.refresh();
                  this.objectTypeChange();
                });
              }

              if (this.resourceTable) {
                this.resourceTable.updateDataSource(true);
              }
            }
          },
          (error: any) => {
            this.errorMsg = error.error;
            this.hasError = true;
          }
        )
      );
    }
  }

  onQueryChange() {
    this.errorMsg = '';
    this.hasError = false;
  }

  onAttributeChange(attributes: Array<AttributeResource>) {
    if (!this.resourceTable) {
      return;
    }

    attributes.forEach((attribute) => {
      if (
        this.tableConfig.columns.findIndex(
          (col) =>
            col.field.toLowerCase() === attribute.systemName.toLowerCase()
        ) < 0
      ) {
        this.tableConfig.columns.push({
          field: attribute.systemName,
          title: attribute.displayName,
          width: 200,
          sortable: true,
        });
      }
    });

    this.tableConfig.columns = this.tableConfig.columns.filter(
      (col) =>
        attributes.findIndex(
          (a) => a.systemName.toLowerCase() === col.field.toLowerCase()
        ) >= 0
    );

    this.tableConfig.exportAttributes = this.tableConfig.columns.map(
      (c) => c.field
    );

    this.resourceTable.updateDataSource(true, false, false);
  }

  onSaveQuery(builder: XpathBuilderComponent) {
    let xpath = '';
    let queryName = '';
    let savingProgress: MatDialogRef<ModalComponent, any>;

    this.subscription.add(
      builder
        .getXpath()
        .pipe(
          switchMap((xpathResult: string) => {
            if (xpathResult) {
              xpath = xpathResult;
            } else {
              xpath = this.resultantQuery;
            }

            const popupConfig: PopupConfig = new PopupConfig();
            popupConfig.width = 420;
            popupConfig.title = 'l10n_saveQuery';
            popupConfig.style = 'outline';
            popupConfig.data = {
              queryName: {
                text: 'l10n_queryName',
                value: '',
                type: 'text',
                validation: `^[a-zA-Z0-9_.-]*$`,
                required: true,
                focused: true,
              },
            };

            return this.modal.popup(popupConfig);
          }),
          switchMap((windowResult: any) => {
            if (!(windowResult instanceof WindowCloseResult)) {
              queryName = windowResult.queryName.value;
              if (this.savedQueries.hasOwnProperty(queryName)) {
                const confirm = this.modal.show(
                  ModalType.confirm,
                  'key_confirmation',
                  'l10n_queryExists'
                );
                return confirm.afterClosed();
              } else {
                return of('yes');
              }
            }

            return EMPTY;
          }),
          switchMap((confirmResult: string) => {
            if (confirmResult && confirmResult === 'yes') {
              savingProgress = this.modal.show(
                ModalType.progress,
                'key_savingChanges',
                '',
                '300px'
              );

              const query = this.utils.DeepCopy(builder.query);
              const attributes = this.resourceTable.config.columns.map(
                (c) => c.field
              );
              if (!this.customViewSetting.hasOwnProperty('savedQueries')) {
                this.customViewSetting.savedQueries = {};
              }
              this.customViewSetting.savedQueries[queryName] = {
                query,
                attributes,
                xpath,
              };
              this.savedQueries = this.customViewSetting.savedQueries;

              return this.resource.updateCustomSettings();
            }

            return EMPTY;
          }),
          finalize(() => {
            if (savingProgress) {
              savingProgress.close();
            }

            this.selectedQuery = null;
          })
        )
        .subscribe(
          () => { },
          (error) => {
            this.errorMsg = error.error;
            this.hasError = true;
          }
        )
    );
  }

  onApplyXPathKeyDown(event: KeyboardEvent, builder: XpathBuilderComponent,
    attributePicker: AttributePickerComponent) {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      this.onApplyXPath(builder, attributePicker);
    }
  }

  onApplyXPath(
    builder: XpathBuilderComponent,
    attributePicker: AttributePickerComponent
  ) {
    if (builder && this.resultantQuery) {
      this.errorMsg = '';
      this.hasError = false;
      this.subscription.add(
        this.resource
          .xpathToJson(this.resultantQuery)
          .pipe(
            tap((result: any) => {
              if (this.utils.validateXPathQuery(result)) {
                const objectType = this.getObjectTypeFromQuery(
                  this.resultantQuery
                );
                this.attributePickerSelectedAttributes = [];
                const reloadAttributePicker = this.queryType !== objectType;
                this.queryType = objectType;
                this.tableConfig.objectType =
                  objectType && objectType !== '*' ? objectType : 'Person';
                this.tableConfig.linkNoneForm = this.queryType === 'event';
                attributePicker.value.forEach((item: any) =>
                  this.attributePickerSelectedAttributes.push(item.systemName)
                );
                if (reloadAttributePicker && attributePicker) {
                  setTimeout(() => {
                    attributePicker.preSelectedItems =
                      this.attributePickerSelectedAttributes;
                    attributePicker.refresh();
                    this.objectTypeChange();
                  });
                }

                builder.query = this.utils.DeepCopy(
                  this.utils.toXPathQuery(result)
                );
                builder.ngOnInit();
              } else {
                if (this.xBuilder) {
                  this.xBuilder.canParse = false;
                }
              }
            }),
            catchError((err: any) => {
              this.errorMsg = err.error;
              this.hasError = true;
              return of(EMPTY);
            }),
            finalize(() => {
              if (this.resourceTable) {
                this.resourceTable.config.query = this.resultantQuery;
                this.resourceTable.updateDataSource(true);
              }
            })
          )
          .subscribe()
      );
    }
  }

  onLoadQuery(
    builder: XpathBuilderComponent,
    attributePicker: AttributePickerComponent
  ) {
    if (builder && this.selectedQuery.value && this.selectedQuery.value.query) {
      this.resultantQuery = this.utils.DeepCopy(this.selectedQuery.value.xpath);
      if (this.selectedQuery.value.query.isNull) {
        if (this.xBuilder) {
          this.xBuilder.canParse = false;
        }
      } else {
        builder.query = this.utils.DeepCopy(this.selectedQuery.value.query);
        builder.ngOnInit();
      }
    }

    if (
      this.resourceTable &&
      this.selectedQuery.value &&
      this.selectedQuery.value.attributes &&
      this.selectedQuery.value.xpath
    ) {
      this.resourceTable.config.columns =
        this.selectedQuery.value.attributes.map((a: string) => {
          return {
            field: a,
            width: 0,
            sortable: true,
          };
        });
      this.resourceTable.config.query = this.selectedQuery.value.xpath;
      this.tableConfig.exportAttributes = this.resourceTable.config.columns.map(
        (c) => c.field
      );
      this.resourceTable.updateDataSource(true);

      const objectType = this.getObjectTypeFromQuery(
        this.resourceTable.config.query
      );
      this.queryType = objectType;
      this.tableConfig.objectType =
        objectType && objectType !== '*' ? objectType : 'Person';
      if (attributePicker) {
        attributePicker.preSelectedItems = this.tableConfig.exportAttributes;
        setTimeout(() => {
          attributePicker.refresh();
        });
      }
    }
  }

  onDeleteQuery() {
    let savingProgress: MatDialogRef<ModalComponent, any>;

    const confirm = this.modal.show(
      ModalType.confirm,
      'key_confirmation',
      'l10n_deleteQuery'
    );

    confirm
      .afterClosed()
      .pipe(
        switchMap((confirmResult) => {
          if (confirmResult && confirmResult === 'yes') {
            savingProgress = this.modal.show(
              ModalType.progress,
              'key_savingChanges',
              '',
              '300px'
            );

            if (
              this.customViewSetting.savedQueries.hasOwnProperty(
                this.selectedQuery.key
              )
            ) {
              delete this.customViewSetting.savedQueries[
                this.selectedQuery.key
              ];
            }
            this.savedQueries = this.customViewSetting.savedQueries;

            return this.resource.updateCustomSettings();
          } else {
            return EMPTY;
          }
        }),
        finalize(() => {
          if (savingProgress) {
            savingProgress.close();
          }
        })
      )
      .subscribe();
  }

  onMenuAction(actionName: string) {
    this.swap.menuEvent(
      new MenuEvent('action', 'search', actionName, {
        selectedItems: this.selectedItems,
        table: this.resourceTable,
      })
    );
  }

  onMenuOpen() {
    this.swap.menuEvent(
      new MenuEvent('open', 'search', '', this.selectedItems, this.actionMenu)
    );
  }
}
